import React, { FC, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { getFilteredCompanies } from '../../../actions/adminActions';
import { searchLegalEntities } from '../../../actions/clustersActions';
import { getAllDirectoryLegalEntities, getAllLegalEntities } from '../../../actions/legalEntitiesActions';
import { ECompanyGroupKey } from '../../../entities/ICompany';
import { EOverviewColumnSubType } from '../../../entities/IDatagrid';
import { EProfileType } from '../../../entities/IGlobal';
import { ISIngleLegalEntity } from '../../../entities/ILegalEntities';
import { IValue } from '../../../entities/IPickers';
import { colorStack } from '../../../styleHelpers/colors';
import { fontSizeAndHeight } from '../../../styleHelpers/fontSizes';
import { useOrganization } from '../../../tools/organizationHooks';
import { useIntlMessage } from '../IntlMessage';
import { Overview } from '../ColoredDatagrid/CellTypeComponents/Overview';
import { IPickerProps, CorePicker, EButtonsTypes } from './CorePicker';

type SearchLegalEntities = ReturnType<typeof searchLegalEntities>;
type GetFilteredCompanies = ReturnType<typeof getFilteredCompanies>;
type GetAllLegalEntities = ReturnType<typeof getAllLegalEntities>;
type GetAllDirectoryLegalEntities = ReturnType<typeof getAllDirectoryLegalEntities>;

export enum EClusteredEntitiesSelection {
    None = 'None',
    CurrentOrganization = 'CurrentOrganization'
}

type IReferencedItem = ISIngleLegalEntity['referencedItems'][number];

const CANNOT_SELECT_MESSAGE_KEYS: Record<string, string> = {
    CorporateCluster: 'legalEntity.clustered.cannotSelect.corporate',
    TPDDCluster: 'legalEntity.clustered.cannotSelect.tpdd'
};

const CANNOT_CREATE_MESSAGE_KEYS: Record<string, string> = {
    CorporateCluster: 'legalEntity.clustered.cannotCreate.corporate',
    TPDDCluster: 'legalEntity.clustered.cannotCreate.tpdd'
};

type IProps = IPickerProps & {
    orgId?: string;
    filteredCompanies?: boolean;
    allCompanies?: boolean;
    filterForDirectory?: string;
    isNotAssignedToCluster?: boolean;
    isNotAssociatedToCluster?: boolean;
    clusteredEntitiesSelection?: EClusteredEntitiesSelection;
    allowClusteredEntities?: boolean;
    onCannotCreateChange?(messageKey: string | undefined, clusterType?: string): void;
};

const Wrapper = styled.div``;

const CLUSTERED_ENTITIES_RESULTS_MAX_HEIGHT = 280;

const ClusterWorkspaces = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
`;

const ClusterWorkspace = styled.div`
    display: flex;
    align-items: center;
    gap: .5rem;
    min-width: 0;
    font-size: .75rem;
    line-height: 1rem;
    color: ${colorStack.darkGrey};
    span {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

const ClusterWorkspaceLogo = styled.img`
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    border-radius: 4px;
    object-fit: cover;
`;

// Sits above the row's selection layer (z-index 999) so the cluster link stays clickable
const ClusterLink = styled.div`
    position: relative;
    z-index: 1000;
`;

const ClusterWarning = styled.div`
    font-size: .6875rem;
    line-height: .875rem;
    color: ${colorStack.middleBlue};
`;

export const LegalEntitiesPicker: FC<React.PropsWithChildren<IProps>> = props => {
    const dispatch = useDispatch();
    const { organization } = useParams<{ organization: string }>();
    const { currentOrganization } = useOrganization(organization);
    const { intlFormatMessage } = useIntlMessage();

    const getData = useCallback((query: string) => {
        if (props.filteredCompanies) {
            return dispatch<GetFilteredCompanies>(getFilteredCompanies(query, 100, ECompanyGroupKey.OurGroup)).then(data => {
                return data?.map(entity => ({
                    key: entity.id,
                    text: entity.name,
                    data: { ...entity, type: EProfileType.LegalEntity }
                }));
            }).catch(() => []);
        } else if (props.allCompanies) {
            return dispatch<GetAllLegalEntities>(getAllLegalEntities(query, undefined, 100, !!props.isNotAssignedToCluster, props.orgId, props.isNotAssociatedToCluster)).then(res => {
                return res.items.map(elem => ({
                    key: elem.id,
                    data: { ...elem, type: EProfileType.LegalEntity },
                    text: elem.name
                }));
            }).catch(() => []);
        } else if (props.filterForDirectory) {
            return dispatch<GetAllDirectoryLegalEntities>(getAllDirectoryLegalEntities(query, { TypeOfLegalEntity: [props.filterForDirectory] }, 100)).then(data => {
                return data?.items?.map(entity => ({
                    key: entity.id,
                    text: entity.name,
                    data: { ...entity, type: EProfileType.LegalEntity }
                }));
            });
        } else {
            return dispatch<SearchLegalEntities>(searchLegalEntities(currentOrganization?.id || props.orgId, query, 50)).then(res => {
                return res.items.map(elem => ({
                    key: elem.id,
                    data: { ...elem, type: EProfileType.LegalEntity },
                    text: elem.name
                }));
            }).catch(() => []);
        }
    }, [props.orgId, props.filterForDirectory, props.filteredCompanies, props.allCompanies]);

    const getBlockingReferencedItem = (data: Partial<ISIngleLegalEntity>): IReferencedItem | undefined => {
        const currentOrganizationId = props.orgId || currentOrganization?.id;
        return data?.referencedItems?.find(item =>
            props.clusteredEntitiesSelection === EClusteredEntitiesSelection.None || item.metadata?.organizationId !== currentOrganizationId
        );
    };

    const getSelectableFirstData = (query: string) =>
        getData(query).then(items => [...items].sort((a, b) =>
            Number(!!getBlockingReferencedItem(a.data)) - Number(!!getBlockingReferencedItem(b.data))
        ));

    const getDisabledItemReason = (data: Partial<ISIngleLegalEntity>): string | undefined => {
        const blockingItem = getBlockingReferencedItem(data);
        return blockingItem
            ? intlFormatMessage({ id: 'legalEntity.clustered.tooltip' }, { organizationName: blockingItem.metadata?.organizationName || '' })
            : undefined;
    };

    const renderItemDetails = (data: Partial<ISIngleLegalEntity>) => {
        const blockingItem = getBlockingReferencedItem(data);
        return blockingItem ? (
            <ClusterWarning>
                {intlFormatMessage({ id: CANNOT_SELECT_MESSAGE_KEYS[blockingItem.type] || 'legalEntity.clustered.cannotSelect.other' })}
            </ClusterWarning>
        ) : undefined;
    };

    const selectElement = (value: IValue[]) => {
        const blockingItem = value?.[0] ? getBlockingReferencedItem(value[0].data) : undefined;
        props.onCannotCreateChange?.(blockingItem ? (CANNOT_CREATE_MESSAGE_KEYS[blockingItem.type] || 'legalEntity.clustered.cannotCreate.other') : undefined, blockingItem?.type);
        props.onSelectElement?.(value);
    };

    const renderItemAside = (data: Partial<ISIngleLegalEntity>) => {
        const workspaces = (data?.referencedItems || [])
            .filter(item => item.metadata?.organizationName || item.metadata?.organizationPicture);
        if (workspaces.length === 0) {
            return undefined;
        }
        return (
            <ClusterWorkspaces>
                {workspaces.map(item => (
                    <ClusterWorkspace key={`${item.type}-${item.metadata?.id}`} title={item.metadata?.organizationName}>
                        {item.metadata?.organizationPicture && <ClusterWorkspaceLogo src={item.metadata.organizationPicture} alt="" />}
                        <span>{item.metadata?.organizationName}</span>
                    </ClusterWorkspace>
                ))}
            </ClusterWorkspaces>
        );
    };

    const renderClusterAside = (data: Partial<ISIngleLegalEntity>) => {
        const clusters = (data?.referencedItems || []).filter(item => item.metadata?.clusterName);
        if (clusters.length === 0) {
            return undefined;
        }
        return (
            <ClusterWorkspaces>
                {clusters.map(item => (
                    <ClusterLink key={`${item.type}-${item.metadata.clusterId}`}>
                        <Overview
                            subType={EOverviewColumnSubType.cluster}
                            fullWidth
                            openInNewTab
                            businessId={Number(item.metadata.businessId)}
                            clusterId={item.metadata.clusterId}
                            clusterName={item.metadata.clusterName}
                            clusterTemplateName={item.metadata.clusterTemplateName}
                            organizationName={item.metadata.organizationName}
                            organizationUrlName={item.metadata.organizationUrlName}
                        />
                    </ClusterLink>
                ))}
            </ClusterWorkspaces>
        );
    };

    const renderItemHeader = (data: Partial<ISIngleLegalEntity>) => {
        const currentOrganizationId = props.orgId || currentOrganization?.id;
        const workspaces = (data?.referencedItems || [])
            .filter(item => item.metadata?.organizationName && item.metadata?.organizationId !== currentOrganizationId);
        if (workspaces.length === 0) {
            return undefined;
        }
        return (
            <ClusterWorkspaces>
                {workspaces.map(item => (
                    <ClusterWorkspace key={`${item.type}-${item.metadata?.id}`} title={item.metadata?.organizationName}>
                        <span>{item.metadata?.organizationName}</span>
                    </ClusterWorkspace>
                ))}
            </ClusterWorkspaces>
        );
    };

    return (
        <Wrapper>
            <CorePicker
                {...props}
                getData={props.clusteredEntitiesSelection ? getSelectableFirstData : getData}
                onSelectElement={props.onCannotCreateChange ? selectElement : props.onSelectElement}
                buttonsType={props.buttonsType || EButtonsTypes.Company}
                getDisabledItemReason={(props.clusteredEntitiesSelection && !props.allowClusteredEntities) ? getDisabledItemReason : props.getDisabledItemReason}
                renderItemDetails={props.clusteredEntitiesSelection ? renderItemDetails : props.renderItemDetails}
                renderItemAside={props.clusteredEntitiesSelection ? renderClusterAside : props.renderItemAside}
                renderItemHeader={props.renderItemHeader}
                resultsMaxHeight={props.resultsMaxHeight || (props.clusteredEntitiesSelection ? CLUSTERED_ENTITIES_RESULTS_MAX_HEIGHT : undefined)}
                fitResultsToAvailableSpace={props.fitResultsToAvailableSpace ?? !!props.clusteredEntitiesSelection}
            />
        </Wrapper>
    );
};